/**
 * @license
 * @copyright Copyright Motili Inc., 2021 All Rights Reserved
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    AgreementStatus,
    CompanyInfo,
    RegisteredContractor,
    SignatureStatus,
    TeamInfo,
    Trade,
} from 'common/types';

type ContactInfo = Pick<
    RegisteredContractor,
    'firstName' | 'lastName' | 'email' | 'phone'
>;

const initialState: { registration: RegisteredContractor } = {
    registration: {
        _id: '',
        signatureId: '',
        signatureStatus: SignatureStatus.NOT_SENT,
        agreements: {},
        created: '',
        lastModified: '',
        uuid: '',
        status: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        trades: [],
        notes: [],
        companyInfo: {
            name: '',
            website: '',
            companyAddress: {
                address1: '',
                address2: '',
                city: '',
                state: '',
                zip: '',
            },
            billingAddress: {
                address1: '',
                address2: '',
                city: '',
                state: '',
                zip: '',
            },
            useCompanyAddressAsBilling: true,
            corporationType: '',
        },
        teamInfo: {
            systemInstalls: '',
            serviceCalls: '',
            masterTechs: '',
            employees: '',
            singleFamilyServiceProvider: false,
            multiDwellingServiceProvider: false,
            commercialServiceProvider: false,
        },
        serviceArea: '',
        documents: {
            epaCertification: [],
            w9: [],
            tradeLicenses: [],
            certificateOfLiability: [],
            codeOfConduct: [],
            companyMSA: [],
            masterServiceAgreement: [],
        },
    },
};

const contractorRegistrationSlice = createSlice({
    name: 'contractorRegistration',
    initialState,
    reducers: {
        setRegistration(state, action: PayloadAction<RegisteredContractor>) {
            state.registration = action.payload;
        },
        setContactInfo(state, action: PayloadAction<ContactInfo>) {
            const { firstName, lastName, email, phone } = action.payload;
            state.registration = {
                ...state.registration,
                firstName,
                lastName,
                email,
                phone,
            };
        },
        setCompanyInfo(state, action: PayloadAction<CompanyInfo>) {
            state.registration.companyInfo = action.payload;
        },
        setTeamInfo(state, action: PayloadAction<TeamInfo>) {
            state.registration.teamInfo = action.payload;
        },
        setServiceArea(state, action: PayloadAction<string>) {
            state.registration.serviceArea = action.payload;
        },
        setTrades(state, action: PayloadAction<Trade[]>) {
            state.registration.trades = action.payload;
        },
        setAgreements(
            state,
            action: PayloadAction<Record<string, AgreementStatus>>
        ) {
            state.registration.agreements = action.payload;
        },
        setMessages(
            state,
            action: PayloadAction<RegisteredContractor['messages']>
        ) {
            state.registration.messages = action.payload;
        },
    },
});

export const {
    setRegistration,
    setContactInfo,
    setCompanyInfo,
    setTeamInfo,
    setServiceArea,
    setTrades,
    setAgreements,
    setMessages,
} = contractorRegistrationSlice.actions;

export default contractorRegistrationSlice.reducer;
